import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Mail, ChevronDown } from "lucide-react";
import { Layout } from "../components/Layout";
import { ContactModal } from "../components/ContactModal";
import { INFO_PAGES, SUPPORT_EMAIL } from "../lib/infoContent";

const Prose = ({ page }) => (
  <div className="space-y-5" data-testid="info-prose">
    {page.blocks.map((b, i) => {
      if (b.ul) return (
        <ul key={i} className="space-y-2">
          {b.ul.map((li, j) => (
            <li key={j} className="flex items-start gap-3 text-[#374151]">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      );
      if (b.email) return (
        <div key={i} className="rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7EA]/50 p-5">
          <p className="text-[#374151]">{b.email}</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 mt-2 font-semibold text-[#B8901F] hover:text-[#8a6d1a]"><Mail size={16} /> {SUPPORT_EMAIL}</a>
        </div>
      );
      return <p key={i} className="text-[#374151] leading-relaxed">{b.p}</p>;
    })}
  </div>
);

const HelpCenter = ({ page, onContact }) => (
  <div data-testid="info-help">
    <div className="grid sm:grid-cols-2 gap-4">
      {page.sections.map((s, i) => (
        <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] transition-shadow" data-testid={`help-section-${i}`}>
          <p className="font-display font-bold text-[#0F0F0F]">{s.title}</p>
          <p className="text-sm text-[#687280] mt-1.5">{s.text}</p>
        </div>
      ))}
    </div>
    <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div>
        <p className="font-display text-lg font-extrabold">{page.contact.title}</p>
        <p className="text-sm text-white/70 mt-1">{page.contact.text}</p>
      </div>
      <button onClick={onContact} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-3 text-sm font-bold hover:bg-[#c5a030] shrink-0" data-testid="help-contact-btn"><Mail size={16} /> Contact Support</button>
    </div>
  </div>
);

const Faq = ({ page }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3" data-testid="info-faq">
      {page.items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="rounded-2xl border border-gray-100 bg-white overflow-hidden" data-testid={`faq-item-${i}`}>
            <button onClick={() => setOpen(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`faq-q-${i}`}>
              <span className="font-semibold text-[#0F0F0F]">{it.q}</span>
              <ChevronDown size={18} className={`text-[#9aa1ab] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && <p className="px-5 pb-5 -mt-1 text-[#687280] leading-relaxed" data-testid={`faq-a-${i}`}>{it.a}</p>}
          </div>
        );
      })}
    </div>
  );
};

export default function InfoPage({ pageKey }) {
  const { slug } = useParams();
  const [contactOpen, setContactOpen] = useState(false);
  const key = pageKey || slug;
  const page = INFO_PAGES[key];

  if (!page) return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-2xl font-extrabold text-[#0F0F0F]">Page not found</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-4 text-[#B8901F] font-semibold"><ArrowLeft size={16} /> Back home</Link>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="info-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#687280] hover:text-[#0F0F0F] mb-6" data-testid="info-back"><ArrowLeft size={15} /> Back home</Link>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0F0F0F] tracking-tight">{page.title}</h1>
        {page.subtitle && <p className="text-[#687280] mt-3 text-lg">{page.subtitle}</p>}
        <div className="mt-8">
          {page.type === "prose" && <Prose page={page} />}
          {page.type === "help" && <HelpCenter page={page} onContact={() => setContactOpen(true)} />}
          {page.type === "faq" && <Faq page={page} />}
        </div>
        {page.type === "prose" && (
          <p className="text-xs text-[#9aa1ab] mt-12">Last updated June 2026 · © 2026 LaunchBS</p>
        )}
      </div>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </Layout>
  );
}
